import { useState, useEffect } from "react";
import SeoHelmet from "@/components/SeoHelmet";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { User, Lock, CreditCard, Trash2, ChevronLeft, ShieldCheck, Mail, Phone, Settings, Zap } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useProfile } from "@/hooks/useProfile";

const SettingsPage = () => {
  const { user, signOut } = useAuth();
  const { profile, loadingProfile } = useProfile();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState<string | null>(null);
  
  // Tab state synced with URL
  const pathParts = location.pathname.split('/').filter(Boolean);
  const activeTab = pathParts.length > 1 ? pathParts[1] : "profile";
  
  const handleTabChange = (value: string) => {
    navigate(`/settings/${value}`);
  };

  // Profile State
  const [firstName, setFirstName] = useState(user?.user_metadata?.first_name || "");
  const [lastName, setLastName] = useState(user?.user_metadata?.last_name || "");
  const [phone, setPhone] = useState(user?.user_metadata?.phone || "");

  // Password State
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    const fetchProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase
        .from('profiles')
        .select('first_name, last_name, phone')
        .eq('id', user.id)
        .single();
      if (data) {
        setFirstName(data.first_name || '');
        setLastName(data.last_name || '');
        setPhone(data.phone || '');
      }
    };
    fetchProfile();
  }, []);

  const handleUpdateProfile = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          first_name: firstName,
          last_name: lastName,
          phone: phone
        }, { onConflict: 'id' });

      if (error) throw error;
      toast.success("Profile updated successfully");
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      if (error) throw error;
      toast.success("Password updated successfully");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm("Are you absolutely sure? This action cannot be undone and all your data will be permanently deleted.");
    if (!confirmed) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("delete-account");
      
      if (error) throw error;
      
      toast.success("Your account has been deleted successfully.");
      await signOut();
      navigate("/");
    } catch (error: any) {
      console.error("Deletion error:", error);
      toast.error(error.message || "Failed to delete account. Please contact support.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (priceId: string, planName: string) => {
    if (!user) return;
    setIsSubscribing(priceId);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout-session", {
        body: { 
          priceId, 
          userId: user.id,
          userEmail: user.email 
        }
      });

      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err: any) {
      console.error("Subscription error:", err);
      toast.error("Could not start checkout: " + (err.message || "Unknown error"));
    } finally {
      setIsSubscribing(null);
    }
  };

  const cancelSubscription = () => {
    toast.info("Please contact support at info@alvaru.com to cancel your subscription.");
  };

  if (!user) {
    navigate("/auth");
    return null;
  }

  return (
    <div className="min-h-screen bg-background text-foreground transition-colors flex flex-col">
      <SeoHelmet
        title="Account Settings"
        description="Update your profile, manage your subscription, and control your data preferences in your Alvaru account settings."
        canonical="https://www.alvaru.com/settings"
        noindex={true}
      />
      {/* Header */}
      <header className="h-16 border-b border-border bg-card/50 backdrop-blur-xl sticky top-0 z-10 px-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/app")} className="hover:bg-muted">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Alvaru" className="h-8 w-auto" />
            <h1 className="font-bold text-lg tracking-tight">Settings</h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <div className="h-8 w-[1px] bg-border mx-1" />
          <div className="flex items-center gap-3 px-3 py-1.5 rounded-full bg-muted/50 border border-border">
            <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-[10px] font-bold text-primary">
              {user.email?.[0].toUpperCase()}
            </div>
            <span className="text-xs font-medium max-w-[120px] truncate">{user.email}</span>
          </div>
        </div>
      </header>

      <main className="flex-1 w-full px-0 py-10 settings-form-container">
        <Tabs 
          value={activeTab} 
          onValueChange={handleTabChange}
          className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-8 items-start"
        >
          <TabsList className="flex md:flex-col h-auto bg-transparent border-none p-0 gap-1 w-full sticky top-24">
            <TabsTrigger 
              value="profile" 
              className="w-full justify-start gap-3 px-4 py-3 rounded-xl data-[state=active]:bg-primary data-[state=active]:text-black transition-all"
            >
              <User className="w-4 h-4" />
              <span className="text-sm font-semibold">Profile</span>
            </TabsTrigger>
            <TabsTrigger 
              value="security" 
              className="w-full justify-start gap-3 px-4 py-3 rounded-xl data-[state=active]:bg-primary data-[state=active]:text-black transition-all"
            >
              <Lock className="w-4 h-4" />
              <span className="text-sm font-semibold">Security</span>
            </TabsTrigger>
            <TabsTrigger 
              value="subscription" 
              className="w-full justify-start gap-3 px-4 py-3 rounded-xl data-[state=active]:bg-primary data-[state=active]:text-black transition-all"
            >
              <CreditCard className="w-4 h-4" />
              <span className="text-sm font-semibold">Subscription</span>
            </TabsTrigger>
            <TabsTrigger 
              value="danger" 
              className="w-full justify-start gap-3 px-4 py-3 rounded-xl data-[state=active]:bg-destructive data-[state=active]:text-destructive-foreground transition-all mt-4"
            >
              <Trash2 className="w-4 h-4" />
              <span className="text-sm font-semibold">Danger Zone</span>
            </TabsTrigger>
          </TabsList>

          <div className="w-full min-w-0">
            {/* Profile Tab */}
            <TabsContent value="profile" className="m-0 focus-visible:outline-none">
              <Card className="border-border shadow-md">
                <CardHeader>
                  <CardTitle>Profile Details</CardTitle>
                  <CardDescription>Update your personal information used in CV generation.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name</Label>
                      <Input 
                        id="firstName" 
                        placeholder="John" 
                        value={firstName} 
                        onChange={(e) => setFirstName(e.target.value)}
                        className="bg-muted/30 focus-visible:ring-primary"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name</Label>
                      <Input 
                        id="lastName" 
                        placeholder="Doe" 
                        value={lastName} 
                        onChange={(e) => setLastName(e.target.value)}
                        className="bg-muted/30 focus-visible:ring-primary"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input id="email" value={user.email} disabled className="pl-10 bg-muted/10 opacity-60 cursor-not-allowed" />
                    </div>
                    <p className="text-[10px] text-muted-foreground italic">Email changes are currently restricted.</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input 
                        id="phone" 
                        placeholder="+1 (555) 000-0000" 
                        value={phone} 
                        onChange={(e) => setPhone(e.target.value)}
                        className="pl-10 bg-muted/30 focus-visible:ring-primary"
                      />
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="border-t border-border pt-6">
                  <Button 
                    onClick={handleUpdateProfile} 
                    disabled={loading}
                    className="bg-primary hover:bg-primary/90 text-black font-bold uppercase tracking-widest text-[11px] px-8 py-6 rounded-xl shadow-lg shadow-primary/20"
                  >
                    {loading ? "Saving Changes..." : "Save Profile"}
                  </Button>
                </CardFooter>
              </Card>
            </TabsContent>

            {/* Security Tab */}
            <TabsContent value="security" className="m-0 focus-visible:outline-none">
              <Card className="border-border shadow-md">
                <CardHeader>
                  <CardTitle>Security</CardTitle>
                  <CardDescription>Secure your account with a new password.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">New Password</Label>
                    <Input 
                      id="newPassword" 
                      type="password" 
                      value={newPassword} 
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="bg-muted/30"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <Input 
                      id="confirmPassword" 
                      type="password" 
                      value={confirmPassword} 
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="bg-muted/30"
                    />
                  </div>
                </CardContent>
                <CardFooter className="border-t border-border pt-6">
                  <Button 
                    onClick={handleChangePassword} 
                    disabled={loading || !newPassword}
                    className="bg-primary hover:bg-primary/90 text-black font-bold uppercase tracking-widest text-[11px] px-8 py-6 rounded-xl shadow-lg shadow-primary/20"
                  >
                    {loading ? "Updating..." : "Update Password"}
                  </Button>
                </CardFooter>
              </Card>
            </TabsContent>

            {/* Subscription Tab */}
            <TabsContent value="subscription" className="m-0 focus-visible:outline-none">
              <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 w-full">
                {/* 1. CURRENT PLAN CARD */}
                <Card className="border-border bg-card/40 backdrop-blur-sm overflow-hidden relative border-l-4 border-l-primary w-full shadow-md">
                  <div className="p-8">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                      <div className="flex items-center gap-5">
                        <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary shadow-[0_0_20px_rgba(0,212,255,0.1)]">
                          <Zap className="w-8 h-8" />
                        </div>
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <h4 className="text-2xl font-bold tracking-tight font-heading">{profile?.plan || 'Free'} Plan</h4>
                            <span className="px-2.5 py-0.5 rounded-full bg-green-500/10 text-green-500 text-[9px] font-bold uppercase tracking-widest border border-green-500/20">Active</span>
                          </div>
                          <p className="text-sm text-muted-foreground flex items-center gap-2">
                            ${profile?.plan === 'Elite' ? '19' : profile?.plan === 'Pro' ? '9' : '0'} <span className="text-xs">/ month</span>
                            {profile?.next_billing_date && (
                              <>
                                <span className="w-1 h-1 rounded-full bg-muted-foreground/30" />
                                Next billing: {new Date(profile.next_billing_date).toLocaleDateString()}
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                      
                      <Button 
                        variant="outline" 
                        className="border-primary/20 text-primary hover:bg-primary hover:text-black font-bold uppercase tracking-widest text-[10px] h-10 px-6 rounded-xl transition-all"
                        onClick={() => window.open('https://billing.stripe.com', '_blank')}
                      >
                        Manage Billing
                      </Button>
                    </div>

                    {/* Progress Bar Area */}
                    <div className="mt-10 p-6 bg-muted/20 rounded-2xl border border-border/50">
                      <div className="flex items-center justify-between mb-3 text-left">
                        <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest font-heading">Monthly Usage</p>
                        <p className="text-sm font-bold font-heading text-primary">
                          {profile?.credits_used || 0} / {profile?.credits_total || 5} <span className="text-[10px] text-muted-foreground uppercase tracking-widest font-normal">CVs Optimised</span>
                        </p>
                      </div>
                      <div className="h-2.5 w-full bg-muted rounded-full overflow-hidden shadow-inner">
                        <div 
                          className="h-full bg-primary shadow-[0_0_15px_rgba(0,212,255,0.5)] transition-all duration-1000 rounded-full" 
                          style={{ width: `${Math.min(100, ((profile?.credits_used || 0) / (profile?.credits_total || 5)) * 100)}%` }}
                        />
                      </div>
                      <p className="mt-3 text-[10px] text-muted-foreground italic">Credits reset on your next billing cycle.</p>
                    </div>
                  </div>
                </Card>

                {/* 2. PLAN CARDS (3 COLUMNS) */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {[
                    { 
                      name: 'Free', 
                      price: '0', 
                      credits: 5, 
                      features: ['5 CV rewrites', 'Extension access', 'Basic PDF', 'Tracker'],
                      color: 'muted'
                    },
                    { 
                      name: 'Pro', 
                      price: '9', 
                      credits: 200, 
                      features: ['200 CV rewrites', 'Extension access', 'Unlimited Auto-apply', 'Priority Support'],
                      color: 'primary',
                      badge: 'Most Popular',
                      priceId: 'price_1TPMFO85c4rhaB2kdbd5Alhl'
                    },
                    { 
                      name: 'Elite', 
                      price: '19', 
                      credits: 800, 
                      features: ['800 CV rewrites', 'Everything in Pro', 'Multiple Base CVs', 'Direct AI Chat'],
                      color: 'primary',
                      priceId: 'price_1TPMGu85c4rhaB2kIyrtQMF7'
                    }
                  ].map((p) => {
                    const isCurrent = (profile?.plan || 'Free') === p.name;
                    const isPaid = p.name !== 'Free';
                    
                    return (
                      <Card 
                        key={p.name} 
                        className={`flex flex-col h-full overflow-hidden transition-all duration-300 relative shadow-sm ${isCurrent ? 'ring-2 ring-primary border-primary/50 bg-primary/[0.03]' : 'border-border bg-card/30 hover:border-border/80'}`}
                      >
                        {p.badge && (
                          <div className="absolute top-0 right-0 py-1 px-4 bg-primary text-black text-[8px] font-black uppercase tracking-widest rounded-bl-xl font-heading shadow-xl z-20">
                            {p.badge}
                          </div>
                        )}
                        
                        <CardHeader className="pt-8 pb-6">
                          <CardTitle className="flex flex-col gap-1 items-start">
                            <span className={`text-[10px] font-bold uppercase tracking-[0.2em] ${p.color === 'primary' ? 'text-primary' : 'text-muted-foreground'}`}>{p.name}</span>
                            <div className="flex items-baseline gap-1">
                              <span className="text-2xl font-bold font-heading">${p.price}</span>
                              <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-widest font-heading">/mo</span>
                            </div>
                          </CardTitle>
                        </CardHeader>
                        
                        <CardContent className="flex-1 space-y-5">
                          <div className="h-px w-full bg-border/50" />
                          <ul className="space-y-3">
                            {p.features.map((f, i) => (
                              <li key={i} className="flex items-center gap-3 text-[11px] text-foreground/80 font-body">
                                <div className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${p.color === 'primary' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                                  <ShieldCheck className="w-2.5 h-2.5" />
                                </div>
                                {f}
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                        
                        <CardFooter className="pt-6 pb-8">
                          {isCurrent ? (
                            <div className="w-full flex flex-col items-center gap-2">
                              <span className="text-[9px] font-black text-primary uppercase tracking-[0.2em]">Active Now</span>
                              <Button disabled className="w-full h-11 border-primary/20 bg-primary/10 text-primary disabled:opacity-100 font-bold uppercase tracking-widest text-[10px] rounded-xl border">
                                Current Plan
                              </Button>
                            </div>
                          ) : (
                            <Button 
                              onClick={() => {
                                if (p.name === 'Free') navigate('/#pricing');
                                else if (p.priceId) handleSubscribe(p.priceId, p.name);
                              }}
                              disabled={isSubscribing !== null}
                              variant={p.color === 'primary' ? 'default' : 'outline'}
                              className={`w-full h-11 font-bold uppercase tracking-widest text-[10px] rounded-xl transition-all ${p.color === 'primary' ? 'bg-primary hover:bg-primary/90 text-black shadow-lg shadow-primary/10' : 'border-border/50 hover:bg-muted'}`}
                            >
                              {isSubscribing === p.priceId ? 'Redirecting...' : isPaid ? (p.price > (p.name === 'Pro' ? '9' : '19') ? 'Downgrade' : 'Upgrade') : 'Switch to Free'}
                            </Button>
                          )}
                        </CardFooter>
                      </Card>
                    );
                  })}
                </div>

                {/* 3. CANCEL LINK */}
                {(profile?.plan && profile?.plan !== 'Free') && (
                  <div className="pt-4 pb-12 flex justify-center">
                    <button 
                      onClick={cancelSubscription}
                      className="group flex flex-col items-center gap-1.5 transition-all text-muted-foreground hover:text-red-400"
                    >
                      <span className="text-[9px] font-bold uppercase tracking-widest opacity-60 group-hover:opacity-100">Danger Zone</span>
                      <span className="text-[11px] font-semibold border-b border-muted-foreground/30 group-hover:border-red-400/50 pb-0.5">Cancel Monthly Subscription</span>
                    </button>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Danger Zone Tab */}
            <TabsContent value="danger" className="m-0 focus-visible:outline-none">
              <Card className="border-destructive/20 shadow-md bg-destructive/5">
                <CardHeader>
                  <CardTitle className="text-destructive flex items-center gap-2">
                    <Trash2 className="w-5 h-5" />
                    Danger Zone
                  </CardTitle>
                  <CardDescription>Permanently delete your account and all associated data.</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    This will remove your access to the Alvaru dashboard, sync between devices, and delete all your saved CVs and history. This action is irreversible.
                  </p>
                </CardContent>
                <CardFooter className="border-t border-destructive/10 pt-6">
                  <Button 
                    variant="destructive" 
                    onClick={handleDeleteAccount}
                    className="font-bold uppercase tracking-widest text-[11px] px-8 h-12 rounded-xl"
                  >
                    Delete My Account
                  </Button>
                </CardFooter>
              </Card>
            </TabsContent>
          </div>
        </Tabs>
      </main>

      <footer className="p-8 text-center border-t border-border">
        <p className="text-xs text-muted-foreground uppercase tracking-[0.2em]">Alvaru AI © 2026</p>
      </footer>
    </div>
  );
};

export default SettingsPage;
